export interface PresenceScheduleInterval {
  start: string;
  end: string;
}

export interface PresenceZone {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** Внутреннее представление (UI). */
export interface PresenceSettings {
  enabled: boolean;
  timeout: number;
  interval: number;
  schedule: PresenceScheduleInterval[];
  zone: PresenceZone | null;
}

export const DEFAULT_PRESENCE_SETTINGS: PresenceSettings = {
  enabled: true,
  timeout: 60,
  interval: 300,
  schedule: [
    { start: '09:00', end: '12:00' },
    { start: '13:00', end: '18:00' },
  ],
  zone: null,
};

function parseZone(raw: unknown): PresenceZone | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const x = o.x;
  const y = o.y;
  const w = o.w;
  const h = o.h;
  if (
    typeof x === 'number' &&
    typeof y === 'number' &&
    typeof w === 'number' &&
    typeof h === 'number' &&
    w > 0 &&
    h > 0
  ) {
    return { x, y, w, h };
  }
  return null;
}

function toTimeInputValue(value: string): string {
  const parts = value.trim().split(':');
  if (parts.length >= 2) {
    return `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}`;
  }
  return value;
}

function parseSchedule(raw: unknown): PresenceScheduleInterval[] {
  if (!Array.isArray(raw)) return [];
  const out: PresenceScheduleInterval[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const o = item as Record<string, unknown>;
    const start = typeof o.start === 'string' ? toTimeInputValue(o.start) : '';
    const end = typeof o.end === 'string' ? toTimeInputValue(o.end) : '';
    if (start && end) {
      out.push({ start, end });
    }
  }
  return out;
}

function readNonNegativeInt(...values: unknown[]): number | undefined {
  for (const v of values) {
    if (typeof v === 'number' && Number.isFinite(v) && v >= 0) {
      return Math.floor(v);
    }
  }
  return undefined;
}

/**
 * Тело PATCH /api/cameras/{camera_id}/presence (внешний API).
 * @see absence_timeout_sec, screenshot_interval_sec, schedule, pre_crop
 */
export function toPresenceApiPayload(settings: {
  enabled?: boolean;
  timeout?: number;
  interval?: number;
  absenceTimeoutSec?: number;
  screenshotIntervalSec?: number;
  schedule?: PresenceScheduleInterval[];
  zone?: PresenceZone | null;
}): Record<string, unknown> {
  const payload: Record<string, unknown> = {};

  if (settings.enabled !== undefined) {
    payload.enabled = settings.enabled;
  }

  const absenceTimeout =
    settings.absenceTimeoutSec !== undefined ? settings.absenceTimeoutSec : settings.timeout;
  if (absenceTimeout !== undefined) {
    payload.absence_timeout_sec = absenceTimeout;
  }

  const screenshotInterval =
    settings.screenshotIntervalSec !== undefined ? settings.screenshotIntervalSec : settings.interval;
  if (screenshotInterval !== undefined) {
    payload.screenshot_interval_sec = screenshotInterval;
  }

  if (settings.schedule !== undefined) {
    payload.schedule = settings.schedule;
  }

  if (settings.zone !== undefined) {
    payload.pre_crop = settings.zone;
  }

  return payload;
}

/** Нормализация ответа GET /presence. */
export function normalizePresenceSettings(data: unknown): PresenceSettings {
  const o = data && typeof data === 'object' ? (data as Record<string, unknown>) : {};
  const zone =
    parseZone(o.zone) ??
    parseZone(o.roi) ??
    parseZone(o.workplace_zone) ??
    parseZone(o.pre_crop);

  const timeout =
    readNonNegativeInt(o.absence_timeout_sec, o.timeout) ?? DEFAULT_PRESENCE_SETTINGS.timeout;
  const interval =
    readNonNegativeInt(o.screenshot_interval_sec, o.interval) ?? DEFAULT_PRESENCE_SETTINGS.interval;

  return {
    enabled: Boolean(o.enabled ?? o.active),
    timeout,
    interval,
    schedule: parseSchedule(o.schedule).length > 0 ? parseSchedule(o.schedule) : [...DEFAULT_PRESENCE_SETTINGS.schedule],
    zone,
  };
}
